import React, { useEffect, useRef, useState } from "react";
import { FileVisibility } from "../services/filesService";
import SharePopover from "./SharePopover";
import EmbedInNotion from "./EmbedInNotion";
import {
  LinkIcon,
  LockedIcon,
  UnlockedIcon,
} from "../../packages/excalidraw/components/icons";
import { glyph } from "./shareIcons";

interface ShareProps {
  fileId?: string;
  visibility?: FileVisibility;
  onSetVisibility: (next: FileVisibility) => Promise<unknown>;
  onCollabDialogOpen?: () => void;
  onSaveRequest?: () => void;
  /**
   * A live-collab room is currently running. Signed-in users get the Share
   * popover *instead of* the upstream LiveCollaborationTrigger, so without
   * this they'd have no indication at all that a session is active.
   */
  isCollaborating?: boolean;
}

/**
 * The Share popover with its "Embed in Notion" extension slot already wired.
 * Shared between the anchored desktop trigger ({@link ShareControl}) and the
 * mobile modal (App.tsx) so both surfaces show the identical popover — the
 * mobile share entry point is not a fork, just this same content re-hosted.
 */
export const SharePopoverWithEmbed: React.FC<ShareProps> = ({
  fileId,
  visibility,
  onSetVisibility,
  onCollabDialogOpen,
  onSaveRequest,
  isCollaborating = false,
}) => {
  const shownVisibility: FileVisibility = visibility ?? "private";
  return (
    <SharePopover
      fileId={fileId}
      visibility={shownVisibility}
      onSetVisibility={onSetVisibility}
      onCollabDialogOpen={onCollabDialogOpen}
      isCollaborating={isCollaborating}
      onSaveRequest={fileId ? undefined : onSaveRequest}
      embedSlot={
        fileId ? (
          <EmbedInNotion
            fileId={fileId}
            visibility={shownVisibility}
            onSetVisibility={onSetVisibility}
          />
        ) : (
          <button
            type="button"
            data-testid="share-embed-slot-disabled"
            disabled
            title="Save this mindmap to embed it in Notion"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4rem",
              width: "100%",
              padding: "0.45rem 0.75rem",
              borderRadius: "0.5rem",
              border: "1px solid var(--default-border-color)",
              background: "transparent",
              color: "var(--text-primary-color)",
              fontWeight: 600,
              fontSize: "0.82rem",
              opacity: 0.6,
              cursor: "not-allowed",
            }}
          >
            {glyph(LinkIcon)}
            Embed in Notion
          </button>
        )
      }
    />
  );
};

/**
 * The editor's Share entry point: a small button that toggles the
 * {@link SharePopover}. Kept as its own component so App.tsx wiring stays a
 * single mount point (minimises merge surface with the parallel My Files
 * slice).
 *
 * `fileId`/`visibility` are omitted for a scratch/unsaved canvas — the
 * popover still renders (so Share stays discoverable) but every action is
 * shown disabled with a "Save this mindmap to share it" prompt instead of
 * hiding the whole feature.
 *
 * `onCollabDialogOpen`, when provided, is forwarded to the popover as the
 * "Live collaborate" action — the same handler App.tsx wires to the
 * top-right trigger and main menu, so starting a room from here is
 * identical to starting it from anywhere else.
 *
 * `onSaveRequest`, when provided, is forwarded to the popover's unsaved-state
 * prompt and runs the existing "Save to My Files" flow (App.tsx already owns
 * that dialog; this is just another entry point into it).
 */
export const ShareControl: React.FC<ShareProps> = (props) => {
  const { visibility, isCollaborating = false } = props;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const shownVisibility: FileVisibility = visibility ?? "private";

  // Dismiss on an outside click or Escape — same listener pattern as the My
  // Files card menu (a backdrop <div> would sit over the row and swallow
  // clicks). Only wired while open.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    // Anchor for the absolutely-positioned popover. The popover floats BELOW
    // this button and no longer participates in the top-right row's flow, so
    // opening it doesn't widen the row or shove Logout around.
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        data-testid="share-control-button"
        aria-expanded={open}
        data-collaborating={isCollaborating ? "true" : "false"}
        title={
          isCollaborating
            ? "Live collaboration is active — open Share"
            : undefined
        }
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          // Matches the Save/Logout `Button` primitive it sits beside: same
          // height, radius and horizontal padding, so the three read as one row
          // of controls rather than three sizes.
          height: "var(--lg-button-size)",
          padding: "0 0.9rem",
          borderRadius: "var(--border-radius-lg)",
          border: "none",
          // Active-session green, mirroring LiveCollaborationTrigger.active.
          // A shade darker than that trigger's #0fb884 because this button
          // carries a *text* label: white on #0fb884 is only 2.6:1, whereas
          // #047857 (same emerald family) is 5.5:1.
          background: isCollaborating ? "#047857" : "var(--color-primary)",
          color: isCollaborating ? "#fff" : "var(--color-icon-white)",
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "var(--shadow-island, 0 1px 4px rgba(0,0,0,0.15))",
        }}
      >
        <span aria-hidden style={{ lineHeight: 0 }}>
          {shownVisibility === "public"
            ? glyph(UnlockedIcon)
            : glyph(LockedIcon)}
        </span>
        Share
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 0.5rem)",
            right: 0,
            zIndex: 1000,
          }}
        >
          <SharePopoverWithEmbed {...props} />
        </div>
      )}
    </div>
  );
};

export default ShareControl;
